/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Switch,
  FlatList,
  Animated,
  RefreshControl,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  MaterialCommunityIcons,
  Ionicons,
  FontAwesome5,
  Feather,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { formatDistanceToNow, format } from 'date-fns';

const NotificationsScreen = () => {
  const navigation = useNavigation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [settings, setSettings] = useState({
    pushEnabled: true,
    emailEnabled: true,
    soundEnabled: true,
    vibrationEnabled: true,
    courseReminders: true,
    liveSessionReminders: true,
    assignmentDeadlines: true,
    newContentAlerts: true,
    weeklyProgress: true,
    promotionalOffers: false,
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [showRead, setShowRead] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [darkMode, setDarkMode] = useState(false);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const notificationSubscription = useRef(null);
  const responseSubscription = useRef(null);

  const categories = [
    { id: 'all', label: 'All', icon: 'grid' },
    { id: 'courses', label: 'Courses', icon: 'book-open-variant' },
    { id: 'live', label: 'Live', icon: 'video' },
    { id: 'deadlines', label: 'Deadlines', icon: 'calendar-clock' },
    { id: 'progress', label: 'Progress', icon: 'chart-line' },
    { id: 'system', label: 'System', icon: 'cog' },
  ];

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
  } : {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
  };

  useEffect(() => {
    loadInitialData();
    setupNotificationListener();
    
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
    
    return () => {
      // Clean up notification listeners
      if (notificationSubscription.current) {
        notificationSubscription.current.remove();
      }
      if (responseSubscription.current) {
        responseSubscription.current.remove();
      }
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadNotifications();
      loadDarkModePreference();
    }, [])
  );

  const setupNotificationListener = () => {
    // Listen for notifications received while app is foregrounded
    notificationSubscription.current = Notifications.addNotificationReceivedListener(handleNotificationReceived);
    
    // Listen for notification responses (user taps on notification)
    responseSubscription.current = Notifications.addNotificationResponseReceivedListener(handleNotificationResponse);
  };

  const handleNotificationReceived = (notification) => {
    console.log('Notification received:', notification);
    // Add the new notification to the list
    addNewNotification(notification);
  };

  const handleNotificationResponse = (response) => {
    console.log('Notification response:', response);
    const { data } = response.notification.request.content;
    
    // Handle notification tap based on data
    if (data?.courseId) {
      navigation.navigate('CourseDetails', { courseId: data.courseId });
    } else if (data?.liveSessionId) {
      navigation.navigate('LiveVideoSession', { sessionId: data.liveSessionId });
    } else if (data?.type === 'live-session-reminder') {
      navigation.navigate('LiveSessionsScreen');
    }
  };

  const addNewNotification = (notification) => {
    const newNotification = {
      id: `push_${Date.now()}`,
      title: notification.request.content.title || 'New Notification',
      message: notification.request.content.body || '',
      type: 'push',
      category: 'system',
      date: new Date().toISOString(),
      read: false,
      icon: 'bell',
      color: '#667eea',
      data: notification.request.content.data,
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      updateUnreadCount(updated);
      saveNotifications(updated);
      return updated;
    });
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadNotifications(),
        loadSettings(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const storedNotifications = await AsyncStorage.getItem('userNotifications');
      let allNotifications = storedNotifications ? JSON.parse(storedNotifications) : [];
      
      // If no notifications, create sample data
      if (allNotifications.length === 0) {
        allNotifications = generateSampleNotifications();
        await AsyncStorage.setItem('userNotifications', JSON.stringify(allNotifications));
      }
      
      // Sort by date (newest first)
      allNotifications.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setNotifications(allNotifications);
      updateUnreadCount(allNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const saveNotifications = async (notificationsList) => {
    try {
      await AsyncStorage.setItem('userNotifications', JSON.stringify(notificationsList));
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  };

  const generateSampleNotifications = () => {
    const now = new Date();
    return [
      {
        id: '1',
        title: '🎉 Welcome to Engineering Learning Hub!',
        message: 'Start your learning journey with our interactive courses and live sessions.',
        type: 'welcome',
        category: 'system',
        date: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        icon: 'party-popper',
        color: '#667eea',
        action: {
          type: 'navigate',
          screen: 'LearningDashboard',
        },
      },
      {
        id: '2',
        title: '🔴 LIVE NOW: Python for Engineers Workshop',
        message: 'Join Dr. Sarah Johnson for an interactive Python programming workshop.',
        type: 'live',
        category: 'live',
        date: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
        read: false,
        icon: 'video',
        color: '#ef4444',
        data: {
          courseId: 'LVC1',
          liveSessionId: 'live-123',
        },
      },
      {
        id: '3',
        title: '📅 Assignment Due Tomorrow',
        message: 'Structural Analysis Assignment 3 is due tomorrow at 11:59 PM.',
        type: 'deadline',
        category: 'deadlines',
        date: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        read: false,
        icon: 'calendar-alert',
        color: '#f59e0b',
        action: {
          type: 'navigate',
          screen: 'Assignments',
        },
      },
      {
        id: '4',
        title: '📊 Weekly Progress Report',
        message: 'You\'ve completed 68% of your courses this week. Keep going!',
        type: 'progress',
        category: 'progress',
        date: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        icon: 'chart-line',
        color: '#10b981',
        action: {
          type: 'navigate',
          screen: 'ProgressReport',
        },
      },
      {
        id: '5',
        title: '🎓 New Course Available: Machine Learning Fundamentals',
        message: 'Enroll now to learn machine learning techniques for engineering.',
        type: 'course',
        category: 'courses',
        date: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        icon: 'book-open',
        color: '#8b5cf6',
        data: {
          courseId: 'ML101',
        },
      },
      {
        id: '6',
        title: '🏆 Certificate Earned!',
        message: 'Congratulations! You\'ve earned the Python Programming Certificate.',
        type: 'achievement',
        category: 'progress',
        date: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        icon: 'trophy',
        color: '#fbbf24',
        action: {
          type: 'navigate',
          screen: 'Certificates',
        },
      },
      {
        id: '7',
        title: '👥 Study Group Invitation',
        message: 'You\'ve been invited to join the "Advanced Structural Analysis" study group.',
        type: 'community',
        category: 'courses',
        date: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        icon: 'account-group',
        color: '#06b6d4',
        action: {
          type: 'navigate',
          screen: 'StudyGroups',
        },
      },
      {
        id: '8',
        title: '💡 Engineering Book Recommendation',
        message: 'Check out "Fundamentals of Electric Circuits" - perfect for your current studies.',
        type: 'resource',
        category: 'courses',
        date: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        read: true,
        icon: 'book-open-page-variant',
        color: '#ec4899',
        action: {
          type: 'navigate',
          screen: 'EngineeringBooksScreen',
        },
      },
    ];
  };

  const loadSettings = async () => {
    try {
      const storedSettings = await AsyncStorage.getItem('notificationSettings');
      if (storedSettings) {
        setSettings(JSON.parse(storedSettings));
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const updateUnreadCount = (notificationsList) => {
    const unread = notificationsList.filter(n => !n.read).length;
    setUnreadCount(unread);
    
    // Update app badge count
    if (Platform.OS !== 'web') {
      Notifications.setBadgeCountAsync(unread);
    }
  };

  const refreshNotifications = async () => {
    setIsRefreshing(true);
    await loadNotifications();
    setIsRefreshing(false);
  };

  const markAsRead = async (notificationId) => {
    try {
      const updatedNotifications = notifications.map(notification =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      );
      
      setNotifications(updatedNotifications);
      updateUnreadCount(updatedNotifications);
      await saveNotifications(updatedNotifications);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    Alert.alert(
      'Mark All as Read',
      'Are you sure you want to mark all notifications as read?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Mark All',
          style: 'default',
          onPress: async () => {
            try {
              const updatedNotifications = notifications.map(notification => ({
                ...notification,
                read: true,
              }));
              
              setNotifications(updatedNotifications);
              updateUnreadCount(updatedNotifications);
              await saveNotifications(updatedNotifications);
            } catch (error) {
              console.error('Error marking all notifications as read:', error);
            }
          },
        },
      ]
    );
  };

  const deleteNotification = async (notificationId) => {
    Alert.alert(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedNotifications = notifications.filter(
                notification => notification.id !== notificationId
              );
              
              setNotifications(updatedNotifications);
              updateUnreadCount(updatedNotifications);
              await saveNotifications(updatedNotifications);
            } catch (error) {
              console.error('Error deleting notification:', error);
            }
          },
        },
      ]
    );
  };

  const clearAllNotifications = async () => {
    Alert.alert(
      'Clear All Notifications',
      'Are you sure you want to clear all notifications? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear All',
          style: 'destructive',
          onPress: async () => {
            try {
              await AsyncStorage.removeItem('userNotifications');
              setNotifications([]);
              setUnreadCount(0);
              
              // Clear badge count
              if (Platform.OS !== 'web') {
                Notifications.setBadgeCountAsync(0);
              }
            } catch (error) {
              console.error('Error clearing all notifications:', error);
            }
          },
        },
      ]
    );
  };

  const handleNotificationPress = async (notification) => {
    // Mark as read
    await markAsRead(notification.id);
    
    // Handle action
    if (notification.action) {
      switch (notification.action.type) {
        case 'navigate':
          navigation.navigate(notification.action.screen);
          break;
        case 'url':
          // Handle URL opening
          break;
        default:
          break;
      }
    }
    
    // Handle data
    if (notification.data) {
      if (notification.data.courseId) {
        navigation.navigate('CourseDetails', { courseId: notification.data.courseId });
      } else if (notification.data.liveSessionId) {
        navigation.navigate('LiveVideoSession', { sessionId: notification.data.liveSessionId });
      }
    }
  };

  const toggleSetting = async (settingKey) => {
    try {
      const newSettings = {
        ...settings,
        [settingKey]: !settings[settingKey],
      };
      
      setSettings(newSettings);
      await AsyncStorage.setItem('notificationSettings', JSON.stringify(newSettings));
      
      // Update Expo notification settings
      if (settingKey === 'pushEnabled' || settingKey === 'soundEnabled' || settingKey === 'vibrationEnabled') {
        updateExpoNotificationSettings(newSettings);
      }
    } catch (error) {
      console.error('Error toggling setting:', error);
    }
  };

  const updateExpoNotificationSettings = async (currentSettings) => {
    try {
      await Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowBanner: currentSettings.pushEnabled,
          shouldShowList: currentSettings.pushEnabled,
          shouldPlaySound: currentSettings.soundEnabled,
          shouldSetBadge: true,
        }),
      });
    } catch (error) {
      console.error('Error updating Expo notification settings:', error);
    }
  };

  const requestNotificationPermissions = async () => {
    try {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;
      
      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }
      
      if (finalStatus === 'granted') {
        Alert.alert('Success', 'Notification permissions granted!');
        const newSettings = { ...settings, pushEnabled: true };
        setSettings(newSettings);
        await AsyncStorage.setItem('notificationSettings', JSON.stringify(newSettings));
        
        // Get push token
        if (Platform.OS !== 'web') {
          const token = await Notifications.getExpoPushTokenAsync();
          console.log('Push token:', token);
        }
      } else {
        Alert.alert('Permission Required', 'Please enable notifications in your device settings to receive important updates.');
      }
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
    }
  };

  const testNotification = async () => {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: '🔔 Test Notification',
          body: 'This is a test notification from Engineering Learning Hub',
          data: { 
            test: true,
            type: 'test-notification'
          },
          sound: settings.soundEnabled,
        },
        trigger: {
          seconds: 2,
        },
      });
      
      Alert.alert('Test Sent', 'A test notification will appear in 2 seconds.');
    } catch (error) {
      console.error('Error sending test notification:', error);
      Alert.alert('Error', 'Failed to send test notification.');
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (selectedCategory !== 'all' && notification.category !== selectedCategory) {
      return false;
    }
    if (!showRead && notification.read) {
      return false;
    }
    return true;
  });

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'live':
        return 'video';
      case 'deadline':
        return 'calendar-alert';
      case 'progress':
        return 'chart-line';
      case 'course':
        return 'book-open';
      case 'achievement':
        return 'trophy';
      case 'welcome':
        return 'party-popper';
      case 'community':
        return 'account-group';
      case 'resource':
        return 'book-open-page-variant';
      case 'push':
        return 'bell';
      default:
        return 'bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'live':
        return '#ef4444';
      case 'deadline':
        return '#f59e0b';
      case 'progress':
        return '#10b981';
      case 'course':
        return '#8b5cf6';
      case 'achievement':
        return '#fbbf24';
      case 'welcome':
        return '#667eea';
      case 'community':
        return '#06b6d4';
      case 'resource':
        return '#ec4899';
      case 'push':
        return '#667eea';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true });
      } else {
        return format(date, 'MMM dd, yyyy');
      }
    } catch (error) {
      return 'Recently';
    }
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="bell-off" size={64} color={darkMode ? "#4a5568" : "#d1d5db"} />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No notifications
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {selectedCategory !== 'all' 
          ? `No ${categories.find(c => c.id === selectedCategory)?.label.toLowerCase()} notifications`
          : 'You\'re all caught up!'}
      </Text>
      <TouchableOpacity 
        style={[styles.testButton, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
        onPress={testNotification}
      >
        <Text style={[styles.testButtonText, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
          Send Test Notification
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderNotificationItem = ({ item }) => (
    <Animated.View style={{ opacity: fadeAnim }}>
      <TouchableOpacity
        style={[
          styles.notificationItem,
          { 
            backgroundColor: darkMode ? '#2d3748' : '#ffffff',
            borderLeftColor: getNotificationColor(item.type),
            opacity: item.read ? 0.8 : 1,
          }
        ]}
        onPress={() => handleNotificationPress(item)}
        onLongPress={() => deleteNotification(item.id)}
      >
        <View style={styles.notificationHeader}>
          <View style={styles.notificationIconContainer}>
            <LinearGradient
              colors={[getNotificationColor(item.type), `${getNotificationColor(item.type)}80`]}
              style={styles.notificationIconGradient}
            >
              <MaterialCommunityIcons 
                name={getNotificationIcon(item.type)} 
                size={20} 
                color="#ffffff" 
              />
            </LinearGradient>
          </View>
          <View style={styles.notificationContent}>
            <Text style={[styles.notificationTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
              {item.title}
            </Text>
            <Text style={[styles.notificationTime, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {formatDate(item.date)}
            </Text>
          </View>
          {!item.read && (
            <View style={styles.unreadIndicator}>
              <View style={styles.unreadDot} />
            </View>
          )}
        </View>
        
        <Text style={[styles.notificationMessage, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          {item.message}
        </Text>
        
        {item.data && Object.keys(item.data).length > 0 && (
          <View style={styles.notificationData}>
            <Text style={[styles.notificationDataText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              Tap to view details
            </Text>
          </View>
        )}
        
        <View style={styles.notificationActions}>
          <TouchableOpacity 
            style={styles.notificationAction}
            onPress={() => deleteNotification(item.id)}
          >
            <MaterialCommunityIcons name="delete-outline" size={18} color={darkMode ? "#a0aec0" : "#6b7280"} />
          </TouchableOpacity>
          
          {!item.read && (
            <TouchableOpacity 
              style={styles.notificationAction}
              onPress={() => markAsRead(item.id)}
            >
              <MaterialCommunityIcons name="check" size={18} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={[styles.actionText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
                Mark as read
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderSettingItem = ({ label, description, value, onToggle, icon, color }) => (
    <View style={[styles.settingItem, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <View style={styles.settingInfo}>
        <View style={[styles.settingIconContainer, { backgroundColor: `${color}20` }]}>
          <MaterialCommunityIcons name={icon} size={24} color={color} />
        </View>
        <View style={styles.settingText}>
          <Text style={[styles.settingLabel, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            {label}
          </Text>
          <Text style={[styles.settingDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {description}
          </Text>
        </View>
      </View>
      <Switch
        value={value}
        onValueChange={onToggle}
        trackColor={{ false: darkMode ? '#4a5568' : '#d1d5db', true: color }}
        thumbColor="#ffffff"
      />
    </View>
  );

  const renderHeader = () => (
    <LinearGradient
      colors={gradients.primary}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>🔔 Notifications</Text>
        {unreadCount > 0 && (
          <View style={styles.unreadBadge}>
            <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
          </View>
        )}
      </View>
      <TouchableOpacity style={styles.settingsButton} onPress={markAllAsRead}>
        <MaterialCommunityIcons name="check-all" size={24} color="#ffffff" />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderCategoryFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false}
      style={styles.categoryContainer}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryButton,
            selectedCategory === category.id && styles.categoryButtonActive,
            { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
          ]}
          onPress={() => setSelectedCategory(category.id)}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#6b7280')} 
          />
          <Text style={[
            styles.categoryText,
            selectedCategory === category.id && styles.categoryTextActive,
          ]}>
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderTabs = () => (
    <View style={[styles.tabContainer, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <TouchableOpacity
        style={[styles.tabButton, !showRead && styles.tabButtonActive]}
        onPress={() => setShowRead(false)}
      >
        <MaterialCommunityIcons 
          name="circle" 
          size={16} 
          color={!showRead ? '#ef4444' : (darkMode ? '#a0aec0' : '#6b7280')} 
        />
        <Text style={[
          styles.tabText,
          !showRead && styles.tabTextActive,
          { color: darkMode ? '#cbd5e0' : '#6b7280' }
        ]}>
          Unread ({unreadCount})
        </Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.tabButton, showRead && styles.tabButtonActive]}
        onPress={() => setShowRead(true)}
      >
        <MaterialCommunityIcons 
          name="check-circle" 
          size={16} 
          color={showRead ? '#10b981' : (darkMode ? '#a0aec0' : '#6b7280')} 
        />
        <Text style={[
          styles.tabText,
          showRead && styles.tabTextActive,
          { color: darkMode ? '#cbd5e0' : '#6b7280' }
        ]}>
          All
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderSettingsSection = () => (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        ⚙️ Notification Settings
      </Text>
      
      <View style={[styles.settingsCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
        {renderSettingItem({
          label: 'Push Notifications',
          description: 'Receive notifications on your device',
          value: settings.pushEnabled,
          onToggle: () => toggleSetting('pushEnabled'),
          icon: 'bell',
          color: '#667eea',
        })}
        
        {renderSettingItem({
          label: 'Sound',
          description: 'Play sound for notifications',
          value: settings.soundEnabled,
          onToggle: () => toggleSetting('soundEnabled'),
          icon: 'volume-high',
          color: '#10b981',
        })}
        
        {renderSettingItem({
          label: 'Vibration',
          description: 'Vibrate for notifications',
          value: settings.vibrationEnabled,
          onToggle: () => toggleSetting('vibrationEnabled'),
          icon: 'vibrate',
          color: '#f59e0b',
        })}
      </View>
      
      <Text style={[styles.sectionSubtitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
        Notification Types
      </Text>
      
      <View style={[styles.settingsCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
        {renderSettingItem({
          label: 'Course Reminders',
          description: 'Reminders for upcoming courses and lessons',
          value: settings.courseReminders,
          onToggle: () => toggleSetting('courseReminders'),
          icon: 'book-open-variant',
          color: '#8b5cf6',
        })}
        
        {renderSettingItem({
          label: 'Live Session Alerts',
          description: 'Alerts for upcoming live sessions',
          value: settings.liveSessionReminders,
          onToggle: () => toggleSetting('liveSessionReminders'),
          icon: 'video',
          color: '#ef4444',
        })}
        
        {renderSettingItem({
          label: 'Assignment Deadlines',
          description: 'Reminders for upcoming assignments',
          value: settings.assignmentDeadlines,
          onToggle: () => toggleSetting('assignmentDeadlines'),
          icon: 'calendar-clock',
          color: '#f59e0b',
        })}
        
        {renderSettingItem({
          label: 'New Content Alerts',
          description: 'Alerts for new courses and materials',
          value: settings.newContentAlerts,
          onToggle: () => toggleSetting('newContentAlerts'),
          icon: 'new-box',
          color: '#06b6d4',
        })}
        
        {renderSettingItem({
          label: 'Weekly Progress Reports',
          description: 'Weekly summaries of your learning progress',
          value: settings.weeklyProgress,
          onToggle: () => toggleSetting('weeklyProgress'),
          icon: 'chart-line',
          color: '#10b981',
        })}
        
        {renderSettingItem({
          label: 'Promotional Offers',
          description: 'Notifications about special offers and discounts',
          value: settings.promotionalOffers,
          onToggle: () => toggleSetting('promotionalOffers'),
          icon: 'tag',
          color: '#ec4899',
        })}
      </View>
      
      <View style={styles.settingActions}>
        <TouchableOpacity 
          style={[styles.actionButton, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
          onPress={requestNotificationPermissions}
        >
          <MaterialCommunityIcons name="shield-check" size={20} color={darkMode ? "#cbd5e0" : "#6b7280"} />
          <Text style={[styles.actionButtonText, { color: darkMode ? "#cbd5e0" : "#6b7280" }]}>
            Request Permissions
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.actionButton, { backgroundColor: darkMode ? '#4a5568' : '#e5e7eb' }]}
          onPress={testNotification}
        >
          <MaterialCommunityIcons name="bell-ring" size={20} color={darkMode ? "#cbd5e0" : "#6b7280"} />
          <Text style={[styles.actionButtonText, { color: darkMode ? "#cbd5e0" : "#6b7280" }]}>
            Test Notification
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.actionButton, styles.dangerButton]}
          onPress={clearAllNotifications}
        >
          <MaterialCommunityIcons name="trash-can-outline" size={20} color="#ffffff" />
          <Text style={[styles.actionButtonText, { color: '#ffffff' }]}>
            Clear All
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      
      <ScrollView 
        style={styles.scrollView}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshNotifications}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
      >
        {renderCategoryFilter()}
        {renderTabs()}
        
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
              📨 Recent Notifications
            </Text>
            <TouchableOpacity onPress={clearAllNotifications}>
              <Text style={[styles.clearAllText, { color: '#ef4444' }]}>
                Clear All
              </Text>
            </TouchableOpacity>
          </View>
          
          {filteredNotifications.length > 0 ? (
            <FlatList
              data={filteredNotifications}
              renderItem={renderNotificationItem}
              keyExtractor={(item) => item.id}
              scrollEnabled={false}
              contentContainerStyle={styles.notificationsList}
            />
          ) : (
            renderEmptyState()
          )}
        </View>
        
        {renderSettingsSection()}
        
        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  unreadBadge: {
    backgroundColor: '#ef4444',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginLeft: 12,
    minWidth: 24,
    alignItems: 'center',
  },
  unreadBadgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  settingsButton: {
    padding: 8,
  },
  scrollView: {
    flex: 1,
  },
  categoryContainer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: 'transparent',
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  categoryButtonActive: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  categoryText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
  },
  categoryTextActive: {
    color: '#ffffff',
  },
  tabContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  tabButtonActive: {
    backgroundColor: '#f3f4f6',
    borderColor: '#d1d5db',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
  },
  tabTextActive: {
    fontWeight: '600',
  },
  section: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  sectionSubtitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  clearAllText: {
    fontSize: 14,
    fontWeight: '600',
  },
  notificationsList: {
    paddingBottom: 8,
  },
  notificationItem: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  notificationIconContainer: {
    marginRight: 12,
  },
  notificationIconGradient: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  notificationTime: {
    fontSize: 12,
  },
  notificationMessage: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  notificationData: {
    padding: 8,
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    borderRadius: 6,
    marginBottom: 12,
  },
  notificationDataText: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  unreadIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#ef4444',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ef4444',
  },
  notificationActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 12,
  },
  notificationAction: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 16,
  },
  actionText: {
    fontSize: 12,
    marginLeft: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
    borderRadius: 16,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },
  testButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  testButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  settingsCard: {
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  settingText: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
  settingActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 4,
  },
  dangerButton: {
    backgroundColor: '#ef4444',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
  },
  bottomPadding: {
    height: 40,
  },
});

export default NotificationsScreen;